import React, { useRef } from 'react';
import { forTemplate } from 'app/common/lib/util/i18n';
import cx from 'classnames';
import {
  IconButton,
  ButtonTypes,
} from 'app/common/components/controls/button/button';
import { IconName, IconColor } from 'app/common/components/controls';
import { Popover, usePopover } from 'app/gamma/src/components/popover';
import { CreateCardFromTemplatePopover } from 'app/src/components/CardTemplatesPopover';
import Backbone from 'app/common/vendor/backbone';
import { useCreateCardFromTemplateMutation } from '@trello/graphql/generated';
import styles from './TemplateCardBanner.less';
import { trackUe, Noun, Verb } from '@trello/analytics';

const format = forTemplate('card_detail');

interface TemplateCardBannerProps {
  idCard: string;
  editable?: boolean;
  hasCover?: boolean;
  hasStickers?: boolean;
}

export const CARD_BACK_CREATE_FROM_TEMPLATE_NAME = Symbol(
  'CARD_BACK_CREATE_FROM_TEMPLATE',
);

export const TemplateCardBanner: React.FunctionComponent<TemplateCardBannerProps> = ({
  idCard,
  editable,
  hasCover,
  hasStickers,
}) => {
  const ref = useRef(null);
  const { isPopoverShown, closePopover, showPopover } = usePopover({
    name: CARD_BACK_CREATE_FROM_TEMPLATE_NAME,
  });

  const [createCardMutation] = useCreateCardFromTemplateMutation();

  return (
    <div
      className={cx(styles.templateCardBanner, {
        [styles.hasCover]: hasCover,
        [styles.hasStickers]: hasStickers,
      })}
    >
      <span className={styles.templateCardBannerImage} />
      <h3 className={styles.templateCardBannerText}>
        {format('this-is-a-template-card')}
      </h3>
      {editable ? (
        <IconButton
          className={styles.templateCardBannerButton}
          icon={IconName.TemplateCreate}
          color={IconColor.Light}
          buttonType={ButtonTypes.PRIMARY}
          ref={ref}
          onClick={() => {
            if (isPopoverShown) {
              closePopover();
            } else {
              trackUe({
                category: Noun.CARD_TEMPLATES,
                verb: Verb.OPENS,
                directObj: Noun.CREATE_CARD_FROM_TEMPLATE_MENU,
                context: {
                  entry: Noun.CARD_BACK,
                },
              });
              showPopover();
            }
          }}
        >
          {format('create-card-from-template')}
        </IconButton>
      ) : null}

      {isPopoverShown ? (
        <Popover
          title={format('create-card-from-template')}
          name={CARD_BACK_CREATE_FROM_TEMPLATE_NAME}
          target={() => ref.current}
          onClose={() => closePopover()}
          contentClassName={styles.popoverPadding}
        >
          {() => (
            <CreateCardFromTemplatePopover
              idCard={idCard}
              showLists
              hideEditButton
              createCard={async (
                name,
                idCardSource,
                keepFromSource,
                idList,
              ) => {
                if (!idList) {
                  return;
                }

                const { data } = await createCardMutation({
                  variables: {
                    idCardSource,
                    idList,
                    name,
                    keepFromSource,
                  },
                });

                const card = data && data.copyCard ? data.copyCard : null;

                if (card) {
                  trackUe({
                    category: Noun.CARD_TEMPLATES,
                    verb: Verb.COMPLETES,
                    directObj: Noun.CREATE_CARD_FROM_TEMPLATE_MENU,
                    context: {
                      entry: Noun.CARD_BACK,
                    },
                  });
                  closePopover();
                  Backbone.history.navigate(`/c/${card.shortLink}`, {
                    trigger: true,
                  });
                }
              }}
            />
          )}
        </Popover>
      ) : null}
    </div>
  );
};
